// src/pages/TermsPage.jsx
import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';

const LAST_UPDATED = 'April 2026';

const Section = ({ title, children }) => (
  <section style={s.section}>
    <h2 style={s.sectionTitle}>{title}</h2>
    {children}
  </section>
);
const P = ({ children }) => <p style={s.para}>{children}</p>;
const Li = ({ children }) => <li style={s.li}>{children}</li>;

export default function TermsPage() {
  useEffect(() => { document.title = 'Terms of Service — OhAPotato'; window.scrollTo(0, 0); }, []);
  return (
    <div style={s.page}>
      <nav style={s.nav}><Link to="/" style={s.navHome}>← Back to OhAPotato</Link></nav>
      <main style={s.main}>
        <header style={s.header}>
          <h1 style={s.title}>Terms of Service</h1>
          <p style={s.meta}>Last updated: {LAST_UPDATED}</p>
          <p style={s.intro}>
            Please read these terms carefully before using OhAPotato. By accessing or using our service
            you agree to be bound by these terms.
          </p>
        </header>

        <Section title="1. Acceptance of terms">
          <P>By using ohapotato.com you confirm that you are at least 13 years old and that you accept these Terms of Service in full. If you do not agree with any part of these terms, please do not use the service.</P>
        </Section>

        <Section title="2. Description of service">
          <P>OhAPotato is a free web application that helps users find recipes based on ingredients they already have at home. The service includes a recipe finder, meal planner, grocery list, pantry tracker, and cooking guides. Features may change or be discontinued at any time without notice.</P>
        </Section>

        <Section title="3. User accounts">
          <P>Some features require creating an account via Auth0. You are responsible for maintaining the security of your account credentials. You agree not to share your account with others or use another person's account without permission.</P>
          <P>We reserve the right to terminate or suspend accounts that violate these terms, engage in abusive behaviour, or attempt to damage or circumvent the service.</P>
        </Section>

        <Section title="4. Acceptable use">
          <P>You agree not to:</P>
          <ul style={s.ul}>
            <Li>Use the service for any unlawful purpose</Li>
            <Li>Attempt to gain unauthorised access to any part of the service or its infrastructure</Li>
            <Li>Scrape, crawl, or automate requests to the service in a way that places excessive load on our systems</Li>
            <Li>Reverse engineer, decompile, or attempt to extract the source code of the service</Li>
            <Li>Use the service to transmit spam, malware, or any other harmful content</Li>
            <Li>Impersonate any person or entity or misrepresent your affiliation with any person or entity</Li>
          </ul>
        </Section>

        <Section title="5. Intellectual property">
          <P>All content, design, and code comprising OhAPotato is owned by or licensed to us and is protected by applicable intellectual property laws. The cooking guides and articles published on the site are original works and may not be reproduced without permission.</P>
          <P>Recipe results are sourced from third-party APIs (Edamam, Spoonacular, Google Gemini). The copyright in those recipes belongs to the respective recipe creators and databases.</P>
        </Section>

        <Section title="6. Third-party services">
          <P>OhAPotato integrates with third-party services including Auth0 (authentication), Edamam (recipe data), Spoonacular (recipe data), Google Gemini (AI features), and Resend (email). Use of these services is also governed by their respective terms of service and privacy policies.</P>
          <P>We are not responsible for the availability, accuracy, or content of third-party services.</P>
        </Section>

        <Section title="7. Disclaimer of warranties">
          <P>The service is provided "as is" and "as available" without warranties of any kind, either express or implied. We do not warrant that the service will be uninterrupted, error-free, or free of viruses or other harmful components.</P>
          <P>Recipe suggestions are generated by automated systems and AI. We make no warranty that any recipe is accurate, complete, safe, or suitable for any particular dietary need or health condition. Always exercise your own judgment when preparing food.</P>
        </Section>

        <Section title="8. Limitation of liability">
          <P>To the maximum extent permitted by law, OhAPotato and its operators shall not be liable for any indirect, incidental, special, consequential, or punitive damages arising out of or related to your use of the service, including but not limited to food preparation outcomes, dietary decisions, or data loss.</P>
          <P>Our total liability for any claim arising out of or relating to these terms or the service shall not exceed the amount you paid us in the twelve months preceding the claim, or £10 if you have not made any payments.</P>
        </Section>

        <Section title="9. Changes to terms">
          <P>We may update these terms at any time. We will indicate the date of the most recent update at the top of this page. Continued use of the service after changes constitutes acceptance of the updated terms.</P>
        </Section>

        <Section title="10. Governing law">
          <P>These terms are governed by and construed in accordance with the laws of England and Wales. Any disputes shall be subject to the exclusive jurisdiction of the courts of England and Wales.</P>
        </Section>

        <Section title="11. Contact">
          <P>If you have questions about these terms, please contact us via the <Link to="/contact" style={s.link}>contact page</Link> or email <a href="mailto:hello@ohapotato.com" style={s.link}>hello@ohapotato.com</a>.</P>
        </Section>

        <footer style={s.footer}>
          <Link to="/" style={s.footerLink}>Home</Link>
          <span style={s.footerDot}>·</span>
          <Link to="/privacy" style={s.footerLink}>Privacy Policy</Link>
          <span style={s.footerDot}>·</span>
          <Link to="/disclaimer" style={s.footerLink}>Disclaimer</Link>
          <span style={s.footerDot}>·</span>
          <Link to="/contact" style={s.footerLink}>Contact</Link>
        </footer>
      </main>
    </div>
  );
}

const s = {
  page: { minHeight:'100vh', background:'var(--bg-base)', color:'var(--text-primary)', transition:'background 0.3s, color 0.3s' },
  nav: { padding:'16px 32px', borderBottom:'1px solid var(--border-base)' },
  navHome: { fontFamily:'var(--font-body)', fontSize:'13px', color:'var(--accent-orange)', textDecoration:'none', fontWeight:'600' },
  main: { maxWidth:'720px', margin:'0 auto', padding:'48px 24px 80px' },
  header: { marginBottom:'48px', paddingBottom:'28px', borderBottom:'1px solid var(--border-base)' },
  title: { fontFamily:'var(--font-display)', fontSize:'clamp(26px,4vw,36px)', fontWeight:'700', marginBottom:'8px', letterSpacing:'-0.4px' },
  meta: { fontFamily:'var(--font-body)', fontSize:'12px', color:'var(--text-muted)', marginBottom:'16px', textTransform:'uppercase', letterSpacing:'1px' },
  intro: { fontFamily:'var(--font-body)', fontSize:'16px', color:'var(--text-secondary)', lineHeight:'1.7' },
  section: { marginBottom:'40px' },
  sectionTitle: { fontFamily:'var(--font-display)', fontSize:'18px', fontWeight:'600', color:'var(--text-primary)', marginBottom:'14px', paddingBottom:'8px', borderBottom:'1px solid var(--border-base)' },
  para: { fontFamily:'var(--font-body)', fontSize:'15px', color:'var(--text-secondary)', lineHeight:'1.75', marginBottom:'14px' },
  ul: { paddingLeft:'20px', marginBottom:'14px' },
  li: { fontFamily:'var(--font-body)', fontSize:'15px', color:'var(--text-secondary)', lineHeight:'1.75', marginBottom:'8px' },
  link: { color:'var(--accent-orange)', textDecoration:'none' },
  footer: { marginTop:'64px', paddingTop:'24px', borderTop:'1px solid var(--border-base)', display:'flex', gap:'12px', alignItems:'center', justifyContent:'center', flexWrap:'wrap' },
  footerLink: { fontFamily:'var(--font-body)', fontSize:'13px', color:'var(--text-muted)', textDecoration:'none' },
  footerDot: { color:'var(--text-muted)', fontSize:'11px' },
};
